<script lang="ts">
    import type {UUID} from 'src/typings';
    import type {Row} from '@tanstack/svelte-table';
    import type {HierarchicalRecordRow} from '../../lib/types';
    import type {Readable} from 'svelte/store';
    import {readable} from 'svelte/store';
    import type {SubRowCreateParam} from 'src/features/record/field-types/types';
    import type {RecordGridDataManager} from 'src/features/record-grid/lib/record-grid-data-manager';
    import {isNotEmpty, sequence} from 'shared/utils/array-utils';
    import {assertExists} from 'shared/utils/custom-utils';
    import {getAncestorRowAtDepth, isLastSubRow} from 'shared/ui-widgets/grid/utils';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import HierarchyHorizontalArrowPart from '../misc/HierarchyHorizontalArrowPart.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpDropdownMenuButton from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuButton.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';

    export let recordGridDataManager: RecordGridDataManager;
    export let row: Row<HierarchicalRecordRow>;
    export let createParams: SubRowCreateParam[];
    export let rowsWithSubrowsLoading$: Readable<UUID[]> = readable([]);
    export let expandButtonHidden = false;
    export let rowCreationDisabled = false;

    $: currentRecordRow = row.original;
    $: subrowsAreLoading = $rowsWithSubrowsLoading$.includes(currentRecordRow.id);
    $: canHaveSubrow = createParams.length > 0 && !currentRecordRow.draft;
    $: isExpanded = row.getIsExpanded();
    $: canExpand = row.getCanExpand() && canHaveSubrow;
    $: depth = row.depth;

    function shouldDisplayVerticalConnector(currentRow: Row<HierarchicalRecordRow>, depthLevel: number): boolean {
        const ancestorRowAtDepth = getAncestorRowAtDepth(currentRow, depthLevel);
        return !isLastSubRow(ancestorRowAtDepth);
    }

    function createSubrow({fieldTypeId, referenceTargetFond}: SubRowCreateParam) {
        assertExists(createParams);
        row.toggleExpanded(true);
        recordGridDataManager.createSubRow(referenceTargetFond, fieldTypeId, currentRecordRow);
    }
</script>

<div class="kp-grid-tree-view-cell">
    {#if depth > 0}
        {#each sequence(1, depth - 1) as depthLevel}
            <div class="offset-container">
                {#if shouldDisplayVerticalConnector(row, depthLevel)}
                    <div class="vertical-connector"/>
                {/if}
            </div>
        {/each}

        <div class="hierarchy-arrow-container">
            <div class="horizontal-arrow-part">
                <HierarchyHorizontalArrowPart/>
            </div>

            <div class="vertical-arrow-part" class:last-expanded={isLastSubRow(row)}></div>
        </div>
    {/if}

    {#if !expandButtonHidden}
        <div class="expand-button-container">
            {#if canExpand}
                <KpButton additionalClasses="toggle-expand-btn"
                          buttonStyle="no-background"
                          buttonSize="xs"
                          isDisabled={!canExpand}
                          on:click={row.getToggleExpandedHandler()}>

                    <div class="arrow-container" class:opened={isExpanded}>
                        <UIcon icon="angle-small-down"/>
                    </div>
                </KpButton>
            {/if}

            {#if subrowsAreLoading}
                <KpLoadingBlock size="xs"/>
            {/if}
        </div>
    {/if}


    {#if canHaveSubrow && isNotEmpty(createParams)}
        {#if (createParams.length === 1)}
            {@const createParam = createParams.at(0)}
            <KpButton additionalClasses="add-new-row-btn"
                      buttonStyle="no-background"
                      buttonSize="xs"
                      isDisabled={rowCreationDisabled}
                      on:click={() => createSubrow(createParam)}>
                <UIcon icon="add" color="var(--success-green)"/>
            </KpButton>
        {:else}
            <KpDropdownMenuButton additionalButtonClasses="add-new-row-btn"
                                  buttonStyle="no-background"
                                  buttonSize="xs"
                                  defaultPlacement="top-end"
                                  isDisabled={rowCreationDisabled}>

                <svelte:fragment slot="button">
                    <UIcon icon="add" color="var(--success-green)"/>
                </svelte:fragment>

                <svelte:fragment slot="menu">
                    {#each createParams as createParam (`${createParam.referenceTargetFond.id}-${createParam.fieldTypeId}`)}
                        <KpDropdownMenuItem on:click={() => createSubrow(createParam)}>
                            {createParam.referenceTargetFond.text} (#{createParam.referenceTargetFond.id}) [{createParam.fieldTypeId}]
                        </KpDropdownMenuItem>
                    {/each}
                </svelte:fragment>
            </KpDropdownMenuButton>
        {/if}
    {/if}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    @hierarchy-tree-section-width: 32px;
    @hierarchy-arrow-color: #ADACB2;
    @hierarchy-arrow-thickness: 2px;
    @hierarchy-arrow-border-radius: (@hierarchy-arrow-thickness / 2);

    .kp-grid-tree-view-cell {
        display: flex;
        overflow: hidden;
        height: 100%;
        gap: @spacing-xs;

        --hierarchy-arrow-color: @hierarchy-arrow-color;

        :global(.add-new-row-btn){
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .expand-button-container {
        width: @hierarchy-tree-section-width;
        align-content: center;

        :global(& .toggle-expand-btn) {
            width: 100%;
        }

        .arrow-container {
            transition: rotate 0.2s ease-in-out;

            &.opened {
                rotate: 180deg;
            }
        }
    }

    .offset-container {
        position: relative;
        width: @hierarchy-tree-section-width;

        display: flex;
        align-items: center;
        justify-content: center;

        .vertical-connector {
            display: flex;
            width: @hierarchy-arrow-thickness;
            border-radius: @hierarchy-arrow-border-radius;
            position: absolute;
            left: 50%;
            background-color: var(--hierarchy-arrow-color);
            transform: translateX(-50%);
            top: 0;
            bottom: 0;
        }
    }

    .hierarchy-arrow-container {
        flex-shrink: 0;
        position: relative;
        width: @hierarchy-tree-section-width;

        .vertical-arrow-part {
            display: flex;
            width: @hierarchy-arrow-thickness;
            border-radius: @hierarchy-arrow-border-radius;
            position: absolute;
            left: 50%;
            background-color: var(--hierarchy-arrow-color);
            transform: translateX(-50%);
            top: 0;
            bottom: 0;

            &.last-expanded {
                bottom: calc(50% - @hierarchy-arrow-border-radius);
            }
        }

        .horizontal-arrow-part {
            display: flex;
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
        }
    }
</style>